import { CalendarOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Tooltip } from "antd";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { DatePicker } from "components/antd-date-picker";
import { DATE_FORMAT_MMDDYYYY } from "utils/dateutils";
import {
  fromProjectCalendarPickerValue,
  toProjectCalendarPickerValue
} from "../project-calendar-date";
import {
  MaterialDateConfirmationMilestone,
  ProjectCalendarOffs
} from "../types";
import {
  composeDisabledDateWithCalendarOffs,
  createActualDateDisabledFn,
  createProjectedDateDisabledFn
} from "./timeline-date-disable";

const PROJECTED_DATE_ORDER_ERROR_TOOLTIP =
  "Projected date must be on or after earlier milestones in the timeline.";

const ACTUAL_DATE_ORDER_ERROR_TOOLTIP =
  "Actual date must be on or after earlier milestones in the timeline.";

export const PREDECESSORS_INCOMPLETE_SET_ACTUAL_TOOLTIP =
  "Associated Submittals have not been completed.";

function MilestoneTimelineCard({
  milestone,
  dataTestId,
  timezoneId,
  calendarOffs,
  isTitleBold = false,
  previousMilestone,
  canMarkComplete,
  projectedDateLabel = "Projected Date",
  disableSetActualDate = false,
  hasProjectedDateError = false,
  hasActualDateError = false,
  onProjectedDateChange,
  onCompletedDateChange,
  onMarkCompleteChange
}: {
  milestone: MaterialDateConfirmationMilestone;
  dataTestId: string;
  timezoneId: string;
  calendarOffs?: ProjectCalendarOffs;
  isTitleBold?: boolean;
  previousMilestone?: MaterialDateConfirmationMilestone;
  canMarkComplete: boolean;
  projectedDateLabel?: string;
  disableSetActualDate?: boolean;
  hasProjectedDateError?: boolean;
  hasActualDateError?: boolean;
  onProjectedDateChange: (date: string | null) => void;
  onCompletedDateChange: (date: string | null) => void;
  onMarkCompleteChange: (isComplete: boolean) => void;
}) {
  const [completedDatePickerOpen, setCompletedDatePickerOpen] = useState(false);
  const isMarkedComplete = Boolean(milestone.actualDate);
  const isSetActualDateDisabled = disableSetActualDate && !isMarkedComplete;

  const projectedDateValue = toProjectCalendarPickerValue(
    milestone.projectedDate,
    timezoneId
  );

  const completedDateValue = toProjectCalendarPickerValue(
    milestone.actualDate,
    timezoneId
  );

  const disabledProjectedDate = useMemo(
    () =>
      composeDisabledDateWithCalendarOffs(
        createProjectedDateDisabledFn(previousMilestone, timezoneId),
        calendarOffs
      ),
    [calendarOffs, previousMilestone, timezoneId]
  );
  const disabledActualDate = useMemo(
    () =>
      composeDisabledDateWithCalendarOffs(
        createActualDateDisabledFn(previousMilestone, timezoneId),
        calendarOffs
      ),
    [calendarOffs, previousMilestone, timezoneId]
  );

  return (
    <div
      className="timeline-milestone-card w-full lg:max-w-[360px] space-y-3"
      data-testid={dataTestId}
    >
      <div
        className={classNames(
          "text-sm text-color-4",
          isTitleBold && "font-semibold"
        )}
      >
        {milestone.name}
      </div>
      <div className="flex items-center gap-2">
        <div className="text-sm text-color-1 shrink-0">
          {projectedDateLabel}
        </div>
        <div className="flex items-center flex-1 min-w-0 gap-1">
          <Tooltip
            title={
              isMarkedComplete
                ? `Clear the Actual Date to modify the ${projectedDateLabel}`
                : null
            }
          >
            <span
              className="inline-block flex-1 min-w-0"
              data-testid={`timeline-projected-date-tooltip-${milestone.id}`}
            >
              <Form.Item
                validateStatus={hasProjectedDateError ? "error" : ""}
                help={null}
                className="timeline-projected-date-form-item mb-0"
              >
                <DatePicker
                  format={DATE_FORMAT_MMDDYYYY}
                  value={projectedDateValue}
                  allowClear={false}
                  disabled={isMarkedComplete}
                  disabledDate={disabledProjectedDate}
                  suffixIcon={<CalendarOutlined className="text-color-6" />}
                  className="w-full"
                  onChange={(date) => {
                    if (!date) {
                      return;
                    }
                    onProjectedDateChange(
                      fromProjectCalendarPickerValue(date, timezoneId)
                    );
                  }}
                  data-testid={`timeline-projected-date-${milestone.id}`}
                />
              </Form.Item>
            </span>
          </Tooltip>
          <span
            className="timeline-projected-date-error-icon-slot shrink-0 w-4 h-4 flex items-center justify-center"
            data-testid={`timeline-projected-date-error-icon-slot-${milestone.id}`}
          >
            {hasProjectedDateError ? (
              <Tooltip title={PROJECTED_DATE_ORDER_ERROR_TOOLTIP}>
                <InfoCircleOutlined
                  className="text-ciq-error-500 hover:text-ciq-error-500 text-sm"
                  data-testid={`timeline-projected-date-error-icon-${milestone.id}`}
                />
              </Tooltip>
            ) : null}
          </span>
        </div>
      </div>
      {(canMarkComplete || isMarkedComplete) && (
        <Tooltip
          title={
            isSetActualDateDisabled
              ? PREDECESSORS_INCOMPLETE_SET_ACTUAL_TOOLTIP
              : null
          }
        >
          <span
            className="inline-block"
            data-testid={`timeline-complete-tooltip-${milestone.id}`}
          >
            <Checkbox
              checked={isMarkedComplete}
              disabled={isSetActualDateDisabled}
              onChange={(event) => {
                onMarkCompleteChange(event.target.checked);
              }}
              data-testid={`timeline-complete-${milestone.id}`}
            >
              Set Actual Date
            </Checkbox>
          </span>
        </Tooltip>
      )}
      {isMarkedComplete && (
        <div className="flex items-center gap-2">
          <div className="text-sm text-color-1 shrink-0">Actual Date</div>
          <div className="flex items-center flex-1 min-w-0 gap-1">
            <Form.Item
              validateStatus={hasActualDateError ? "error" : ""}
              help={null}
              className="timeline-actual-date-form-item mb-0 flex-1 min-w-0"
            >
              <DatePicker
                format={DATE_FORMAT_MMDDYYYY}
                value={completedDateValue}
                allowClear={false}
                open={completedDatePickerOpen}
                onOpenChange={setCompletedDatePickerOpen}
                disabledDate={disabledActualDate}
                suffixIcon={<CalendarOutlined className="text-color-6" />}
                className="w-full"
                onChange={(date) => {
                  if (!date) {
                    return;
                  }
                  onCompletedDateChange(
                    fromProjectCalendarPickerValue(date, timezoneId)
                  );
                }}
                renderExtraFooter={() =>
                  // This picker is rendered only inside the `isMarkedComplete`
                  // guard above, which is Boolean(milestone.actualDate), so the
                  // undefined arm of this ternary can never execute.
                  /* v8 ignore next */
                  milestone.actualDate ? (
                    <div className="flex justify-center py-2">
                      <Button
                        onClick={() => {
                          setCompletedDatePickerOpen(false);
                          onCompletedDateChange(null);
                        }}
                        data-testid={`timeline-clear-completed-date-${milestone.id}`}
                      >
                        Clear Date
                      </Button>
                    </div>
                  ) : undefined
                }
                data-testid={`timeline-completed-date-${milestone.id}`}
              />
            </Form.Item>
            <span
              className="timeline-actual-date-error-icon-slot shrink-0 w-4 h-4 flex items-center justify-center"
              data-testid={`timeline-actual-date-error-icon-slot-${milestone.id}`}
            >
              {hasActualDateError ? (
                <Tooltip title={ACTUAL_DATE_ORDER_ERROR_TOOLTIP}>
                  <InfoCircleOutlined
                    className="text-ciq-error-500 hover:text-ciq-error-500 text-sm"
                    data-testid={`timeline-actual-date-error-icon-${milestone.id}`}
                  />
                </Tooltip>
              ) : null}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default MilestoneTimelineCard;
